import { useRouter } from 'next/navigation';
import { type Dispatch, type SetStateAction, useCallback } from 'react';
import { useModal } from '@src/components/Modal/ModalProvider';
import { useRailItemContext } from '@src/components/Rail/RailItemContext';
import { useSierra } from '@src/components/Sierra/SierraProvider';
import { configPublic } from '@src/config.public';
import { type UseClickActionArgs } from '@src/hooks/useClickAction/useClickActionArgs';
import { useClickActionContext } from '@src/hooks/useClickAction/useClickActionContext';
import { useTrackNavigate } from '@src/hooks/useClickAction/useTrackNavigate';
import { useTrackOpenModal } from '@src/hooks/useClickAction/useTrackOpenModal';
import { useErrors } from '@src/hooks/useErrors';
import { useLocalizedRouter } from '@src/hooks/useLocalizedRouter';
import { useSession } from '@src/hooks/useSession';
import { useLocale } from '@src/i18n/LocaleProvider';
import { isSelectionStateValid, useSKUSelectionStore } from '@src/stores/SKUSelectionStore';
import { BuilderActionErrorsGroup } from '@src/utils/constants.util';
import { log } from '@src/utils/log.util';
import { getShopifyBaseUrl } from '@src/utils/url.util';
interface UseOnClickArgs {
  setVideoOpen: Dispatch<SetStateAction<boolean>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setIsLoginModalOpen: Dispatch<SetStateAction<boolean>>;
  shouldSkip?: boolean;
  useClickActionArgs: UseClickActionArgs;
}
export const useOnClick = (args: UseOnClickArgs) => {
  const {
    setVideoOpen,
    shouldSkip,
    useClickActionArgs,
    setIsLoading,
    setIsLoginModalOpen
  } = args;
  const router = useRouter();
  const {
    actionName,
    addItemsToCart,
    navigate,
    revertUpsell,
    addSelectedSKUsToCart,
    loginModal,
    analyticsModule
  } = useClickActionArgs;
  const {
    setIsLoadingAnyAction
  } = useClickActionContext();
  const {
    locale
  } = useLocale();
  const localizedRouter = useLocalizedRouter();
  const errors = useErrors();
  const {
    sierra
  } = useSierra();
  const {
    setOpen: setModalOpen
  } = useModal();
  const openModal = args?.useClickActionArgs?.openModal;
  const modalId = openModal?.modalId;
  const {
    trackOpenModal
  } = useTrackOpenModal({
    openModal,
    analyticsModule
  });
  const {
    trackNavigate
  } = useTrackNavigate({
    navigate,
    analyticsModule
  });
  const {
    slug: railSlug
  } = useRailItemContext();
  const {
    user
  } = useSession();
  const isLoggedIn = Boolean(user?.email);
  const selectSKU = useSKUSelectionStore(state => state.selectSKU);
  const getSelectedSKUs = useSKUSelectionStore(state_0 => state_0.getSelectedSKUs);
  const selectSKUValue = args?.useClickActionArgs?.selectSKU?.sku;
  const onClick = useCallback(async () => {
    if (shouldSkip) return;
    if (actionName === 'navigate') {
      trackNavigate();
      return;
    }
    if (actionName === 'popoutVideo') {
      setVideoOpen(true);
      return;
    }
    if (actionName === 'openModal' && modalId) {
      setModalOpen(modalId);
      trackOpenModal();
      return;
    }
    if (actionName === 'loginModal') {
      if (!isLoggedIn) {
        setIsLoginModalOpen(true);
        return;
      }
      if (isLoggedIn && loginModal?.redirectUrl) {
        localizedRouter.push(loginModal.redirectUrl);
        return;
      }
    }
    if (actionName === 'selectSKU' && railSlug && selectSKUValue) {
      selectSKU(railSlug, selectSKUValue);
      return;
    }
    if (actionName === 'openChatbotWindow') {
      if (sierra) {
        sierra?.openChatModal();
      } else {
        log.warn('[Click Action] Sierra has not been initialized in this page.');
      }
      return;
    }

    // server actions called below
    try {
      const redirectOrStopLoading = (url?: string) => {
        if (url === '/cart') {
          router.replace('#cart');
          setIsLoading(false);
          setIsLoadingAnyAction(false);
          return;
        }
        if (url) {
          localizedRouter.push(url);
        } else {
          setIsLoading(false);
          setIsLoadingAnyAction(false);
        }
      };
      setIsLoading(true);
      setIsLoadingAnyAction(true);
      if (actionName === 'addItemsToCart') {
        if (configPublic.featureFlags.shopifyEnabled) {
          const shopifyEnv = configPublic.shopify.env;
          const getVariantId = (item: any) => {
            if (shopifyEnv === 'prod' && item.variantIdProduction) return item.variantIdProduction;
            if (shopifyEnv === 'stage' && item.variantIdStage) return item.variantIdStage;
            // Default to variantId for QA and other environments
            return item.variantId;
          };
          const getSubscriptionPlanId = (item_0: any) => {
            if (shopifyEnv === 'prod' && item_0.subscriptionPlanIdProduction) return item_0.subscriptionPlanIdProduction;
            if (shopifyEnv === 'stage' && item_0.subscriptionPlanIdStage) return item_0.subscriptionPlanIdStage;
            // Default to subscriptionPlanId for QA and other environments
            return item_0.subscriptionPlanId;
          };
          const addToCartUrl = new URL(`${getShopifyBaseUrl(locale.code)}/cart`);
          const items = addItemsToCart?.skus?.map(item_1 => ({
            id: getVariantId(item_1),
            quantity: item_1.quantity ?? 1,
            selling_plan: getSubscriptionPlanId(item_1) ?? ''
          }));
          items?.forEach(item_2 => {
            if (!item_2.id) {
              log.warn(`[Click Action] addItemsToCart has empty values — id: "${item_2.id}", quantity: "${item_2.quantity}", selling_plan: "${item_2.selling_plan}" from url ${window.location.href}`);
            }
          });
          addToCartUrl.searchParams.set('addItemsToCart', JSON.stringify({
            items,
            redirectUrl: addItemsToCart?.shopifyRedirectUrl ?? ''
          }));
          // TODO: implement tracking for shopify add to cart
          window.location.href = addToCartUrl.toString();
        } else {
          const skus = addItemsToCart?.skus;
          if (!skus) return;
          redirectOrStopLoading(addItemsToCart?.redirectUrl);
        }
        return;
      }
      if (actionName === 'upsellToCart') {
        return;
      }
      if (actionName === 'revertUpsell') {
        redirectOrStopLoading(revertUpsell?.redirectUrl);
      }
      if (actionName === 'addSelectedSKUsToCart') {
        const selectedSkus = getSelectedSKUs();
        const extraSkus = addSelectedSKUsToCart?.extraSkus?.map(val => val.sku) ?? [];
        const bundleId = addSelectedSKUsToCart?.bundleId;
        const skus_0 = [...selectedSkus, ...extraSkus].map(sku => ({
          sku,
          quantity: 1,
          ...(bundleId ? {
            custom: {
              bundleId
            }
          } : {})
        }));
        const SKUSelectionState = useSKUSelectionStore.getState();
        const isSKUSelectionValid = isSelectionStateValid(SKUSelectionState.groups, SKUSelectionState.groupRestrictions);
        if (!isSKUSelectionValid || !skus_0) {
          setIsLoading(false);
          setIsLoadingAnyAction(false);
          return;
        }
        redirectOrStopLoading(addSelectedSKUsToCart?.redirectUrl);
        return;
      }
    } catch (error: any) {
      setIsLoading(false);
      setIsLoadingAnyAction(false);
      log.error(`[Click Action] Error while performing action - ${error?.message}`, error);
      errors.setError(error, {
        group: BuilderActionErrorsGroup
      });
    }
  }, [shouldSkip, actionName, modalId, railSlug, selectSKUValue, trackNavigate, setVideoOpen, setModalOpen, trackOpenModal, isLoggedIn, loginModal?.redirectUrl, setIsLoginModalOpen, localizedRouter, selectSKU, sierra, setIsLoading, setIsLoadingAnyAction, addItemsToCart, router, locale.code, revertUpsell?.redirectUrl, getSelectedSKUs, addSelectedSKUsToCart?.extraSkus, addSelectedSKUsToCart?.bundleId, addSelectedSKUsToCart?.redirectUrl, errors]);
  return {
    onClick
  };
};